import { Link, useRouterState } from "@tanstack/react-router";
import classNames from "classnames";
import { useContext, useEffect, useRef, useState } from "react";
import { FaCircleUser, FaRightFromBracket } from "react-icons/fa6";

import UserContext from "@/contexts/user";
import Hamburger from "@/components/hamburger";
import {
  getActiveNavSection,
  getSectionDefaultPath,
  getVisibleNavSections,
} from "@/components/navConfig";

const makoLogoLockup = "/mako_by_oceanx_lockup.svg";

type TopNavProps = {
  isMobileNavOpen: boolean;
  onOpenMobileNav: () => void;
  onSignOut: () => void | Promise<void>;
};

export default function TopNav({
  isMobileNavOpen,
  onOpenMobileNav,
  onSignOut,
}: TopNavProps) {
  const { user } = useContext(UserContext);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const visibleSections = getVisibleNavSections(user);
  const activeSection = getActiveNavSection(pathname, user);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isProfileMenuOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        event.target instanceof Node &&
        !profileMenuRef.current.contains(event.target)
      ) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [isProfileMenuOpen]);

  return (
    <header className="fixed left-0 right-0 top-0 z-[120] h-16 bg-blue-500 shadow-md">
      <div className="flex h-full items-center">
        <div className="flex h-full items-center justify-center px-4 lg:w-52 lg:px-0">
          <Link to="/" className="flex-shrink-0">
            <img
              className="h-7 w-auto"
              alt="Mako by OceanX"
              src={makoLogoLockup}
              width={692}
              height={128}
            />
          </Link>
        </div>

        <div className="hidden flex-1 lg:block">
          <div className="flex h-full w-full items-center px-6 lg:px-10">
            <nav className="flex min-w-0 flex-1 flex-row flex-nowrap items-center gap-1 overflow-x-auto">
              {visibleSections.map((section) => {
                const IconComponent = section.icon;
                const defaultTo = getSectionDefaultPath(section, user);
                const isActive = activeSection?.key === section.key;
                const isComingSoon = !defaultTo;

                const sectionContent = (
                  <>
                    {IconComponent && (
                      <IconComponent size={16} className="shrink-0" />
                    )}
                    <span>{section.title}</span>
                    {isComingSoon && (
                      <span className="rounded bg-blue-400/50 px-1.5 py-0.5 text-[0.65rem] uppercase tracking-wide text-zinc-100">
                        Soon
                      </span>
                    )}
                  </>
                );

                const sectionClasses = classNames(
                  "flex min-h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-[background-color,color,transform] duration-150 ease-out active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-50 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-500",
                  isActive
                    ? "bg-white/10 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]"
                    : "text-zinc-200 hover:bg-white/10 hover:text-white",
                  isComingSoon &&
                    "cursor-not-allowed text-zinc-300 hover:bg-transparent"
                );

                if (!defaultTo) {
                  return (
                    <button
                      key={section.key}
                      type="button"
                      className={sectionClasses}
                      aria-disabled="true"
                      tabIndex={-1}
                    >
                      {sectionContent}
                    </button>
                  );
                }

                return (
                  <Link
                    key={section.key}
                    to={defaultTo}
                    className={sectionClasses}
                  >
                    {sectionContent}
                  </Link>
                );
              })}
            </nav>

            <div className="relative ml-3 shrink-0" ref={profileMenuRef}>
              <button
                type="button"
                className="flex h-11 w-11 items-center justify-center rounded-full text-zinc-100 transition-[background-color,color,transform] duration-150 ease-out hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-50 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-500 active:scale-[0.96]"
                aria-label="Open profile menu"
                aria-expanded={isProfileMenuOpen}
                onClick={() => setIsProfileMenuOpen((prev) => !prev)}
              >
                <FaCircleUser size={27} />
              </button>

              {isProfileMenuOpen && (
                <div className="absolute right-0 top-full z-[130] mt-[5px] w-52 overflow-hidden rounded-lg bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.12)]">
                  {/* TODO: Restore Settings when the profile settings page is ready to ship.
                   * <button
                   *   type="button"
                   *   className="flex min-h-10 w-full cursor-not-allowed items-center gap-2 px-3 py-2 text-left text-sm text-zinc-400"
                   *   title="Settings coming soon"
                   *   onClick={() => setIsProfileMenuOpen(false)}
                   * >
                   *   <FaGear size={14} />
                   *   Settings
                   * </button>
                   */}
                  <button
                    type="button"
                    className="group flex min-h-10 w-full items-center gap-2 px-3 py-2 text-left text-sm text-zinc-700 transition-[background-color,color,transform] duration-150 ease-out hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-100 active:scale-[0.96]"
                    onClick={async () => {
                      setIsProfileMenuOpen(false);
                      await onSignOut();
                    }}
                  >
                    <FaRightFromBracket
                      size={14}
                      style={{ transform: "rotate(180deg)" }}
                      className="text-zinc-500 transition-colors duration-150 ease-out group-hover:text-zinc-700"
                    />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="ml-auto flex items-center pr-4 lg:hidden">
          <Hamburger
            isSidebarExpanded={isMobileNavOpen}
            toggleSidebarExpand={onOpenMobileNav}
          />
        </div>
      </div>
    </header>
  );
}
